import React, { useContext, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { AppDispatch, RootState } from "../../../slices/store";
import { jobsGet, jobSingleGet } from "../../../slices/jobs/jobManagementSlice";
import { handleDeleteJobService } from "../service/JobsService";
import { TodosContext } from "../../../context";
import UseAuthConfig from "../../../hooks/useAuthConfig";

import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../components/organisms/Headline/Headline";
import ActionModal from "../../../components/molecules/modal/ActionModal";
import DetailsSection from "../../../components/templates/DetailsSection/DetailsSection";

const contentDetails = [
  { label: "Client", name: "client" },
  { label: "Start Date", name: "startDate" },
  { label: "Job Criteria", name: "criteria" },
  { label: "End Date", name: "endDate" },
  { label: "Job Status", name: "state" },
];

const JobDetails = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { protectedResources }: any = UseAuthConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const { addAlert } = useContext(TodosContext);

  const selectedJob = useSelector(
    (state: RootState) => state.jobsManagement.selectedJob
  );

  const [isConfirmationModalShow, setIsConfirmationModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenModal = () => setIsConfirmationModalShow(true);
  const handleCloseModal = () => setIsConfirmationModalShow(false);

  const jobRequests = useMemo(
    () => ({
      PageNumber: 1,
      SearchText: "",
      SortOrder: 1,
      PageSize: 5,
      OrderBy: "",
      ParameterOne: "",
      ParameterTwo: "",
      ParameterThree: "",
      url: protectedResources.apiTodoList.endpoint,
      accessToken: localStorage.getItem("AccessToken") || "llll",
    }),
    [protectedResources]
  );

  useEffect(() => {
    if (selectedJob) return;

    const id = location.pathname.split("/")[2];

    const params = {
      accessToken: localStorage.getItem("AccessToken") || "llll",
      url: `${protectedResources.apiTodoList.endpoint}/jobs/${id}`,
    };

    setIsLoading(true);
    dispatch(jobSingleGet(params)).finally(() => setIsLoading(false));
  }, [dispatch, location.pathname, protectedResources, selectedJob]);

  const DetailsValueGenerator = (item: { label: string; name: string }) => {
    if (!selectedJob) return "-";

    switch (item.name) {
      case "client":
        return (
          `${selectedJob.customer?.firstName || ""} ${
            selectedJob.customer?.lastName || ""
          }`.trim() || "-"
        );

      case "startDate":
      case "endDate":
        return selectedJob[item.name] || "-";

      default:
        return selectedJob[item.name] || "-";
    }
  };

  const handleMainButtonClick = (buttonType: string) => {
    if (buttonType === "Back" || buttonType === "Cancel") {
      navigate(-1);
      return;
    }

    if (buttonType === "Delete") {
      handleOpenModal();
    }
  };

  const buttons = [
    { name: "Back", className: "btn-secondary" },
    { name: "Delete", className: "btn-primary" },
  ];

  const handleConfirm = () => {
    if (!selectedJob) return;

    handleDeleteJobService(
      selectedJob,
      jobRequests,
      setIsLoading,
      dispatch,
      addAlert,
      true,
      navigate,
      jobsGet,
      protectedResources
    );

    handleCloseModal();
  };

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />

      <Headline
        name="Job details"
        subHeading="Keep track of every job in one place"
      />

      <DetailsSection
        title={selectedJob?.name || ""}
        contentDetails={contentDetails}
        DetailsValueGenerator={DetailsValueGenerator}
        handleMainButtonClick={handleMainButtonClick}
        BUTTONS={buttons}
      />

      <ActionModal
        title="Delete Item"
        show={isConfirmationModalShow}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      >
        <p className="modal-content">
          Are you sure you want to delete this item?
        </p>
      </ActionModal>
    </Container>
  );
};

export default JobDetails;
